import { e1rmAtEffort } from "./pr.js";
import type { LoadableMode } from "./plateCalculator.js";
import {
  listDefaultWeightCandidates,
  pctOfMax,
  type DefaultWeightContext,
} from "./resolveDefaultWeight.js";
import { roundLoadable, type LoadableContext } from "./roundLoadable.js";
import {
  isEffortDerivedWeightSource,
  type WeightSource,
} from "./schemas/weightSource.js";

// #539: the AMRAP test the logger's chip proposes on an ordinary set, the
// way a coach would. Never a 1RM attempt (measurement-contract §14, 24/07):
// a load the athlete can take to failure for about AMRAP_TEST_REPS reps.

/** The reps the proposed load aims at, to failure (~79% by Epley). */
export const AMRAP_TEST_REPS = 8;
/** The rep minimum the test asks for. */
export const AMRAP_TEST_FLOOR = 5;
/** Above this a rep minimum is not shown: past 12 a test says little about
 *  the max. Its own constant, not E1RM_REP_CAP, which #123 may move. */
export const AMRAP_FLOOR_SHOWN_MAX = 12;

export type AmrapTestContext = Omit<DefaultWeightContext, "effort"> & {
  /** The set's display unit: the load floors to that unit's step. */
  unit: "kg" | "lb";
  /** The athlete's plate steps (absent: the defaults). */
  loadable?: LoadableContext;
  /** The exercise's loading shape (`plateLoadModeFor`). */
  mode?: LoadableMode;
};

export type AmrapTestLoad = {
  /** Canonical kg, floored to a load the athlete can build. */
  kg: number;
  /** One of the `effort-from-*` values: where the load came from. */
  source: WeightSource;
  /** The max it was read from: the MAX, or the last set's e1RM. */
  basisKg: number;
  /** `kg` as a whole percent of `basisKg`. */
  pct: number;
};

/** The proposed test load, or null when no instrument can answer. Walks the
 *  default-weight chain with an "8 reps at RIR 0" target and takes the first
 *  `effort-from-*` candidate: the last set's e1RM first, then a MAX. Raw
 *  `history` is never used: it is blind to effort. */
export const amrapTestLoad = (ctx: AmrapTestContext): AmrapTestLoad | null => {
  const { unit, loadable, mode, ...chain } = ctx;
  const pick = listDefaultWeightCandidates(
    { kind: "none" },
    { ...chain, effort: { reps: AMRAP_TEST_REPS, targetRir: 0 } },
  ).find((c) => isEffortDerivedWeightSource(c.source));
  if (!pick) return null;
  const last = chain.lastLogged;
  const basisKg =
    pick.source === "effort-from-history"
      ? last
        ? e1rmAtEffort(last.weightKg, last.reps, last.rir ?? null)
        : null
      : (chain.userMax ?? null);
  if (basisKg === null || !(basisKg > 0)) return null;
  const kg = roundLoadable(pick.kg, unit, loadable, mode);
  return {
    kg,
    source: pick.source,
    basisKg,
    pct: Math.round(pctOfMax(kg, basisKg)),
  };
};
